import React, { useCallback, useEffect, useState } from 'react'
import apiClient from '../../services/api'
import { useToast } from '../../context/ToastContext'
import LoadingSpinner from '../../components/LoadingSpinner'

const faqFilters = [
  { key: 'all', label: 'All FAQs' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' }
]

const initialForm = { question: '', answer: '', active: true }

const AdminFAQ = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(initialForm)
  const [editId, setEditId] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const { addToast } = useToast()

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/cms/faqs')
      setItems(res.data)
    } catch (e) { addToast('Error loading FAQs', 'danger') } finally { setLoading(false) }
  }, [addToast])

  useEffect(() => {
    const initialLoad = async () => {
      try {
        const res = await apiClient.get('/api/cms/faqs')
        setItems(res.data)
      } catch (e) {
        addToast('Error loading FAQs', 'danger')
      } finally {
        setLoading(false)
      }
    }

    initialLoad()
  }, [addToast])

  const save = async (e) => {
    e.preventDefault()
    try {
      if (editId) {
        await apiClient.put(`/api/cms/faqs/${editId}`, form)
      } else {
        await apiClient.post('/api/cms/faqs', form)
      }
      addToast('FAQ saved', 'success')
      setForm(initialForm)
      setEditId(null)
      load()
    } catch (e) { addToast('Error saving FAQ', 'danger') }
  }

  const remove = async (id) => {
    if (!window.confirm('Delete FAQ?')) return
    await apiClient.delete(`/api/cms/faqs/${id}`)
    addToast('Deleted', 'success')
    load()
  }

  if (loading) return <LoadingSpinner />

  const filteredItems = items.filter((item) => {
    if (statusFilter === 'active') return item.active
    if (statusFilter === 'inactive') return !item.active
    return true
  })

  const filterCount = (key) => {
    if (key === 'active') return items.filter((item) => item.active).length
    if (key === 'inactive') return items.filter((item) => !item.active).length
    return items.length
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h2>FAQ Management</h2>
          <p>
            Review active and inactive answers separately so the public FAQ stays tidy while draft content remains easy to update.
          </p>
        </div>
      </div>

      <div className="card p-3 mb-4 shadow-sm admin-form-card">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h5 className="mb-0">{editId ? 'Edit FAQ' : 'Add FAQ'}</h5>
          {editId && (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setForm(initialForm); setEditId(null) }}>
              Cancel Edit
            </button>
          )}
        </div>
        <form onSubmit={save} className="row g-3">
          <div className="col-12">
            <input className="form-control" placeholder="Question" value={form.question} onChange={(event) => setForm({ ...form, question: event.target.value })} required />
          </div>
          <div className="col-12">
            <textarea className="form-control" rows="3" placeholder="Answer" value={form.answer} onChange={(event) => setForm({ ...form, answer: event.target.value })} required />
          </div>
          <div className="col-md-3 d-flex align-items-center">
            <div className="form-check">
              <input type="checkbox" id="activeFaq" className="form-check-input" checked={form.active} onChange={(event) => setForm({ ...form, active: event.target.checked })} />
              <label htmlFor="activeFaq" className="form-check-label">Active</label>
            </div>
          </div>
          <div className="col-md-3">
            <button className="btn btn-primary w-100">{editId ? 'Update' : 'Add'} FAQ</button>
          </div>
        </form>
      </div>

      <div className="admin-toolbar">
        <div className="admin-filter-chips">
          {faqFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`admin-filter-chip${statusFilter === filter.key ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(filter.key)}
            >
              {filter.label}
              <span className="admin-filter-chip-count">{filterCount(filter.key)}</span>
            </button>
          ))}
        </div>
        <div className="admin-note">{filteredItems.length} FAQ{filteredItems.length === 1 ? '' : 's'} shown</div>
      </div>

      <div className="admin-card-list">
        {filteredItems.map((item) => (
          <div className="card shadow-sm" key={item._id}>
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                <h5 className="mb-0">{item.question}</h5>
                <span className={`admin-status-badge ${item.active ? 'admin-status-active' : 'admin-status-inactive'}`}>
                  {item.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="mb-3">{item.answer}</p>
              <div className="admin-action-group">
                <button className="btn btn-sm btn-secondary" onClick={() => { setForm({ question: item.question, answer: item.answer, active: item.active }); setEditId(item._id) }}>Edit</button>
                <button className="btn btn-sm btn-danger" onClick={() => remove(item._id)}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default AdminFAQ
